import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
// CARILOHA: SKU is hidden by default and only revealed for CS reps via the Alt+Shift+U shortcut.
import { isSkuVisible, SKU_VISIBILITY_TOGGLE_EVENT } from '@theme/cariloha-sku-toggle';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    // CARILOHA: reflect the current CS reveal state on first render, then keep it in sync as it's toggled.
    this.applyVisibility();
    window.addEventListener(SKU_VISIBILITY_TOGGLE_EVENT, this.applyVisibility);

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(SKU_VISIBILITY_TOGGLE_EVENT, this.applyVisibility);

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * CARILOHA: Shows the SKU only when the CS reveal flag is on and a SKU is present.
   */
  applyVisibility = () => {
    const hasSku = this.refs.sku.textContent.trim() !== '';
    this.style.display = isSkuVisible() && hasSku ? 'block' : 'none';
  };

  /**
   * Handles product select event and updates the SKU.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (resource) {
          const variantSku = resource.sku || '';
          this.refs.sku.textContent = variantSku;
          // CARILOHA: visibility depends on the CS reveal flag, not just SKU presence.
          this.applyVisibility();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
